import { getAuth } from 'firebase/auth';
import type { FunnelCohort, FunnelCohortCondition, FunnelCohortEstimate } from '../types';

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) {
    return 'https://dev-api.segmently.ai';
  }
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) {
    return 'https://stage-api.segmently.ai';
  }
  return 'https://api.segmently.ai';
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

interface CohortsEnvelope<T> {
  apiVersion: string;
  asOf: string;
  data: T;
  warnings: Array<{ code: string; message: string }>;
}

async function handleResponse<T>(response: Response): Promise<CohortsEnvelope<T>> {
  const text = await response.text().catch(() => '');
  let payload: CohortsEnvelope<T> | { detail?: string; title?: string } | undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON response from cohorts API: HTTP ${response.status}`);
    }
  }

  if (!response.ok) {
    let message: string | undefined;
    if (payload && 'detail' in payload) message = payload.detail;
    else if (payload && 'title' in payload) message = payload.title;
    throw new Error(message || `HTTP ${response.status}`);
  }

  return payload as CohortsEnvelope<T>;
}

export interface CohortInputPayload {
  name: string;
  description?: string;
  conditions: FunnelCohortCondition[];
}

export class FunnelCohortsApiClient {
  constructor(private readonly baseUrl = getBaseUrl()) {}

  private url(projectId: string, suffix = ''): string {
    return `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/cohorts${suffix}`;
  }

  async list(projectId: string, signal?: AbortSignal): Promise<FunnelCohort[]> {
    const response = await fetch(this.url(projectId), { headers: await getAuthHeaders(), signal });
    const payload = await handleResponse<{ cohorts: FunnelCohort[] }>(response);
    return payload.data.cohorts;
  }

  async create(projectId: string, input: CohortInputPayload): Promise<FunnelCohort> {
    const response = await fetch(this.url(projectId), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    const payload = await handleResponse<{ cohort: FunnelCohort }>(response);
    return payload.data.cohort;
  }

  async update(projectId: string, cohortId: string, input: CohortInputPayload): Promise<FunnelCohort> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(cohortId)}`), {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    const payload = await handleResponse<{ cohort: FunnelCohort }>(response);
    return payload.data.cohort;
  }

  async delete(projectId: string, cohortId: string): Promise<void> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(cohortId)}`), {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });
    await handleResponse<{ deleted: boolean }>(response);
  }

  async estimate(
    projectId: string,
    payload: { cohortId?: string; conditions?: FunnelCohortCondition[]; dryRun?: boolean },
    signal?: AbortSignal,
  ): Promise<FunnelCohortEstimate> {
    const response = await fetch(this.url(projectId, '/estimate'), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
      signal,
    });
    const envelope = await handleResponse<FunnelCohortEstimate>(response);
    return envelope.data;
  }
}
